import AuthLayout from "../../../components/layouts/auth";
import InputField from "../../../components/form/input-field";
import { useState } from "react";
import ApiRequest from "../../../lib/ApiCall";
import { useNavigate } from "react-router-dom";
import { SuccessFlash } from "../../../lib/flash-notification";

const CreateMember = () => {

  type Contact = {
    name: string;
    email: string;
    phone: string;
    position: string;
    contact_address: string;
    is_primary: boolean;
  };

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [errors, setErrors] = useState<any>({});

  const [form, setForm] = useState({
    firm_name: "",
    sec_registration_number: "",
    company_address: "",
    contacts: [
      {
        name: "",
        email: "",
        phone: "",
        position: "",
        contact_address: "",
        is_primary: true
      }
    ] as Contact[]
  });

  const handleChange = (field: keyof typeof form, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleContactChange = <K extends keyof Contact>(
    index: number,
    field: K,
    value: Contact[K]
  ) => {
    const updated = [...form.contacts];
    updated[index][field] = value;

    setForm(prev => ({
      ...prev,
      contacts: updated
    }));
  };

  const addContact = () => {
    setForm(prev => ({
      ...prev,
      contacts: [
        ...prev.contacts,
        {
          name: "",
          email: "",
          phone: "",
          position: "",
          contact_address: "",
          is_primary: false
        }
      ]
    }));
  };

  const removeContact = (index: number) => {
    const updated = form.contacts.filter((_, i) => i !== index);

    if (!updated.some(c => c.is_primary) && updated.length > 0) {
      updated[0].is_primary = true;
    }

    setForm(prev => ({ ...prev, contacts: updated }));
  };

  const setPrimaryContact = (index: number) => {
    const updated = form.contacts.map((c, i) => ({
      ...c,
      is_primary: i === index
    }));

    setForm(prev => ({ ...prev, contacts: updated }));
  };

  const handleSubmit = async () => {

    setLoading(true);
    setErrors({});

    const request = await ApiRequest({
      endpoint: "member/create",
      method: "POST",
      payload: form
    });

    setLoading(false);

    // SUCCESS
    if (request.status === "200") {
      SuccessFlash(request.message);
      navigate("/members");
      return;
    }

    // console.log(request.data.response.data.data);
    if (request.status === "error") {
      setErrors(request?.data?.response?.data?.data ?? '');
      return;
    }

    setErrors({
      global: request.message || "Something went wrong"
    });
  };

  // helper to get contact field error
  const getContactError = (index: number, field: string) => {
    return errors?.[`contacts.${index}.${field}`]?.[0];
  };

  return (
    <AuthLayout>
      <div className="max-w-4xl mx-auto">

        <h1 className="text-2xl font-semibold mb-1">Create Member</h1>
        <p className="text-sm text-gray-500 mb-6">
          Add institution and manage contacts
        </p>

        {/* GLOBAL ERROR */}
        {errors.global && (
          <div className="bg-red-50 text-red-600 p-3 rounded mb-4 text-sm">
            {errors.global}
          </div>
        )}

        {/* BASIC INFO */}
        <div className="bg-white shadow-sm rounded-2xl p-6 mb-6 border">

          <h2 className="font-semibold mb-4">Institution Details</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            <div>
              <InputField
                label="Institution Name"
                value={form.firm_name}
                onChangeInput={(e) => handleChange("firm_name", e.target.value)}
              />
              {errors?.firm_name?.[0] && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.firm_name[0]}
                </p>
              )}
            </div>

            <div>
              <InputField
                label="SEC Registration Number"
                value={form.sec_registration_number}
                onChangeInput={(e) =>
                  handleChange("sec_registration_number", e.target.value)
                }
              />
              {errors?.sec_registration_number?.[0] && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.sec_registration_number[0]}
                </p>
              )}
            </div>

            <div className="md:col-span-2">
              <InputField
                label="Company Address"
                value={form.company_address}
                onChangeInput={(e) =>
                  handleChange("company_address", e.target.value)
                }
              />
              {errors?.company_address?.[0] && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.company_address[0]}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* CONTACTS */}
        <div className="bg-white shadow-sm rounded-2xl p-6 mb-6 border">

          <div className="flex justify-between items-center mb-4">
            <h2 className="font-semibold text-lg">Contacts</h2>
            <button onClick={addContact} className="pryBtnOutline w-52">
              + Add Contact
            </button>
          </div>

          {errors?.contacts && (
            <div className="bg-red-50 text-red-600 p-2 rounded mb-3 text-sm">
              {errors.contacts[0]}
            </div>
          )}

          {form.contacts.map((contact, index) => (
            <div
              key={index}
              className={`border rounded-xl p-4 mb-4 ${contact.is_primary ? "border-blue-500 bg-blue-50" : ""
                }`}
            >

              {/* HEADER */}
              <div className="flex justify-between mb-3">
                <span className="text-xs font-medium">
                  {contact.is_primary ? "Primary Contact" : "Contact"}
                </span>

                <div className="flex gap-2">
                  {!contact.is_primary && (
                    <button
                      onClick={() => setPrimaryContact(index)}
                      className="text-xs text-blue-600"
                    >
                      Set Primary
                    </button>
                  )}
                </div>
              </div>

              {/* FORM */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                <div>
                  <InputField
                    label="Name"
                    value={contact.name}
                    onChangeInput={(e) =>
                      handleContactChange(index, "name", e.target.value)
                    }
                  />
                  {getContactError(index, "name") && (
                    <p className="text-red-500 text-xs">
                      {getContactError(index, "name")}
                    </p>
                  )}
                </div>

                <div>
                  <InputField
                    label="Email" type="email"
                    value={contact.email}
                    onChangeInput={(e) =>
                      handleContactChange(index, "email", e.target.value)
                    }
                  />
                  {getContactError(index, "email") && (
                    <p className="text-red-500 text-xs">
                      {getContactError(index, "email")}
                    </p>
                  )}
                </div>

                <InputField
                  label="Phone"
                  value={contact.phone}
                  onChangeInput={(e) =>
                    handleContactChange(index, "phone", e.target.value)
                  }
                />
                <div>
                  <InputField
                    label="Position"
                    value={contact.position}
                    onChangeInput={(e) =>
                      handleContactChange(index, "position", e.target.value)
                    }
                  />
                  {getContactError(index, "position") && (
                    <p className="text-red-500 text-xs">
                      {getContactError(index, "position")}
                    </p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <InputField
                    label="Contact Address"
                    value={contact.contact_address}
                    onChangeInput={(e) =>
                      handleContactChange(index, "contact_address", e.target.value)
                    }
                  />
                </div>
              </div>

              {!contact.is_primary && (
                <button
                  onClick={() => removeContact(index)}
                  className="text-red-500 text-xs mt-3"
                >
                  Remove Contact
                </button>
              )}
            </div>
          ))}
        </div>

        {/* ACTIONS */}
        <div className="flex justify-end gap-3">
          <button
            onClick={() => navigate("/members")}
            className="pryBtnOutline"
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            className="pryBtn"
            disabled={loading}
          >
            {loading ? "Creating..." : "Create Member"}
          </button>
        </div>
      </div>
    </AuthLayout>
  );
};

export default CreateMember;